import {
  ChangeDetectionStrategy,
  Component,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, combineLatest, forkJoin } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { ConfigurationService, IDirectoryAppConfig } from '@sondermind/configuration';
import { EmptyURIValue } from '@sondermind/data-access/directory-link';
import { IconSizes, IconUsageTypes, IrisIconComponent } from '@sondermind/iris/icon';
import { IProviderMenu, StrapiService } from '@sondermind/third-party/strapi';
import { USStateOptions } from '@sondermind/utilities/states-list';
import { PipesModule } from '@sondermind/utilities/pipes';
import { DownloadAppBannerComponent } from '../download-app-banner/download-app-banner.component';
import { SignUpNewsletterFormComponent } from '../signup-newsletter-form/signup-newsletter-form.component';
import { IFooterState, PUBLIC_FOOTER_TABS, PublicFooterViewModel, TabKey } from './models/footer-menu.interface';
import { FALLBACK_INSURANCES, FALLBACK_SPECIALTIES } from './models/footer-static-data';

const PrivacyRegime = {
  GDPR: 'GDPR',
  CALIFORNIA: 'california-1'
} as const;

const ConsentViewState = {
  QUICK_OPTIONS: 'QuickOptions',
  ACCEPT_OR_REJECT: 'AcceptOrRejectAllOrMoreChoices',
  ACCEPT_ALL: 'AcceptAll'
} as const;

/**
 * This footer is used in public-facing applications like the
 * provider-landing-page and provider-directory.
 */
@Component({
  selector: 'public-footer',
  standalone: true,
  imports: [
    CommonModule,
    DownloadAppBannerComponent,
    IrisIconComponent,
    SignUpNewsletterFormComponent,
    PipesModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './public-footer.component.html',
  styleUrls: ['./public-footer.component.scss']
})
export class PublicFooterComponent {
  readonly iconSizes = IconSizes;
  readonly iconUsageTypes = IconUsageTypes;

  emptyUriValue = EmptyURIValue;

  readonly footerTabs = PUBLIC_FOOTER_TABS;

  readonly providerDirectoryUrl: string =
    this.configurationService.env.providerDirectoryBase || 'https://www.sondermind.com/provider-directory';

  private readonly activeTabSubject = new BehaviorSubject<TabKey>('state');
  readonly activeTab$ = this.activeTabSubject.asObservable();

  private readonly openAccordionIdSubject = new BehaviorSubject<string | number | null>(null);
  readonly openAccordionId$ = this.openAccordionIdSubject.asObservable();

  private readonly navigationContent$ = forkJoin({
    footer: this.strapiService.getFooterNavigation$(),
    legal: this.strapiService.getLegalNavigation$(),
    social: this.strapiService.getSocialNavigation$(),
    globalText: this.strapiService.getGlobalText$(),
    providers: this.strapiService.getProviders$()
  }).pipe(
    shareReplay(1)
  );

  private readonly states$ = new BehaviorSubject<IFooterState[]>(
    USStateOptions
      .filter((s) => s.activeProviderState)
      .map((s) => {
        const abbreviation = s.value;
        const name = s.label;

        return {
          id: abbreviation,
          name,
          abbreviation,
          href: `${this.providerDirectoryUrl}/sta-${abbreviation.toLowerCase()}/cty-${this.emptyUriValue}/`
        };
      })
  );

  readonly publicFooterVm$ = combineLatest({
    navigation: this.navigationContent$,
    states: this.states$,
    activeTab: this.activeTab$,
    openAccordionId: this.openAccordionId$
  }).pipe(
    map(({ navigation, states, activeTab, openAccordionId }): PublicFooterViewModel => {
      const prefixIfRelative = (link: string): string => {
        // Keep absolute URLs (http/https) and non-http schemes (mailto/tel).
        if (/^https?:\/\//i.test(link) || /^[a-zA-Z][a-zA-Z0-9+.-]*:/i.test(link)) {
          return link;
        }
        const normalized = link.startsWith('/') ? link : `/${link}`;
        return `${this.marketingBaseUrl}${normalized}`;
      };

      const getInsuranceLink = (slug: string): string => {
        const path = slug === 'medicare' ? `/${slug}/` : `/insurance/${slug}`;
        return `${this.marketingBaseUrl}${path}`;
      };

      const legalButtonsLink = navigation.legal.buttons.map((btn) => ({
        ...btn,
        link: prefixIfRelative(btn.link)
      }));

      const footerMenusLink = navigation.footer.menus.map((menu) => ({
        ...menu,
        link: menu.link ? prefixIfRelative(menu.link) : menu.link,
        buttons: menu.buttons?.map((btn) => ({
          ...btn,
          link: prefixIfRelative(btn.link)
        }))
      }));

      const insurancesLink = this.filterInsurances(navigation.providers).map((insurance) => ({
        ...insurance,
        link: getInsuranceLink(insurance.slug)
      }));

      const specialtiesLink = FALLBACK_SPECIALTIES.map((specialty) => ({
        ...specialty,
        link: `${this.marketingBaseUrl}/${specialty.slug}/`
      }));

      return {
        activeTab,
        openAccordionId,
        states,
        insurances: insurancesLink,
        specialties: specialtiesLink,
        footerMenus: footerMenusLink,
        legalButtons: legalButtonsLink,
        socialButtons: navigation.social.buttons,
        globalText: navigation.globalText ?? null
      };
    }),
    shareReplay(1)
  );

  get marketingBaseUrl(): string {
    const raw = this.configurationService.env.marketingUrl || 'https://www.sondermind.com';
    // Some configs use a host only (e.g. "gatsbyjs-stage.sondermind.biz")
    return raw.startsWith('http://') || raw.startsWith('https://') ? raw : `https://${raw}`;
  }

  constructor(
    private configurationService: ConfigurationService<IDirectoryAppConfig>,
    private strapiService: StrapiService
  ) {}

  setActiveTab(tab: TabKey): void {
    this.activeTabSubject.next(tab);
  }

  toggleAccordion(menuId: string | number): void {
    const current = this.openAccordionIdSubject.getValue();
    this.openAccordionIdSubject.next(current === menuId ? null : menuId);
  }

  private filterInsurances(providers: IProviderMenu[]): IProviderMenu[] {
    const providerSlugs = providers.map((p) => p.slug);

    return providerSlugs.length > 0
      ? FALLBACK_INSURANCES.filter((insurance) => providerSlugs.includes(insurance.slug))
      : FALLBACK_INSURANCES;
  }
  readonly trackById = (index: number, item: { id: string | number; }): string | number => item.id;
  readonly trackBySlug = (index: number, item: { slug: string }): string => item.slug;

  getDataTestId(label: string): string {
    return `menu-button-link-${label.toLowerCase().replace(/\s+/g, '-')}`;
  }
}
